import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { eur, fmtDate, fmtTime, Loading, Stat, ProStatusBadge, EmptyState } from "@/components/common";
import Section from "@/components/Section";
import { useAuth } from "@/context/AuthContext";
import BackNav from "@/components/BackNav";
import ProCalendar from "@/pages/pro/ProCalendar";
import ProDaFare from "@/pages/pro/ProDaFare";
import { CalendarDays, Bell, ClipboardList, CheckCircle2, Users, UserRound } from "lucide-react";

export default function AreaPro() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [completed, setCompleted] = useState([]);
  const [pending, setPending] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [calendar, setCalendar] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [giro, setGiro] = useState([]);
  const [calOpen, setCalOpen] = useState(false);
  const [todoOpen, setTodoOpen] = useState(false);

  const load = () => {
    api.get("/pro/dashboard").then((r) => setData(r.data)).catch((e) => { if (e?.response?.status === 403) setPending(true); });
    api.get("/pro/interventions?status=completata").then((r) => setCompleted(r.data)).catch(() => {});
    api.get("/pro/notifications").then((r) => setNotifs(r.data)).catch(() => {});
    api.get("/pro/calendar").then((r) => setCalendar(r.data)).catch(() => {});
    api.get("/pro/customers").then((r) => setCustomers(r.data)).catch(() => {});
  };
  useEffect(load, []);

  useEffect(() => {
    api.get("/pro/planning").then((r) => setGiro(r.data)).catch(() => {});
  }, []);

  if (pending) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center space-y-4" data-testid="pro-pending">
        <p className="eyebrow">Area professionista</p>
        <h1 className="font-serif text-3xl text-navy">Account in attesa di approvazione</h1>
        <p className="text-sm text-body">Grazie per la registrazione. L'amministratore attiverà il tuo account e ti assegnerà il Comune di riferimento in esclusiva. Potrai iniziare non appena riceverai la conferma.</p>
      </div>
    );
  }

  if (!data) return <Loading />;

  const todayKey = new Date().toLocaleDateString("en-CA");
  const completedToday = completed.filter((i) => i.completed_at && new Date(i.completed_at).toLocaleDateString("en-CA") === todayKey);
  const unreadCount = notifs.filter((n) => !n.read).length;

  const giri = Object.values(
    giro.reduce((acc, g) => {
      const comune = g.cemetery?.comune || "Senza comune";
      const entry = acc[comune] || (acc[comune] = { comune, rows: [] });
      g.items.forEach((i) => entry.rows.push({ ...i, _cemeteryName: g.cemetery?.name }));
      return acc;
    }, {})
  ).sort((a, b) => a.comune.localeCompare(b.comune));

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 space-y-5" data-testid="pro-dashboard">
      <BackNav />
      <div>
        <p className="eyebrow mb-1">Area professionista</p>
        <h1 className="font-serif text-4xl text-navy">Ciao, {user?.name}.</h1>
        {data.comuni?.length > 0 && (
          <p className="text-sm text-muted-soft mt-1" data-testid="pro-comuni">Comuni assegnati in esclusiva: {data.comuni.join(", ")}</p>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat testid="pro-todo" label="Da effettuare" value={data.todo_count} onClick={() => setTodoOpen(true)} />
        <Stat testid="pro-done" label="Completati" value={data.completed_count} />
        <Stat testid="pro-pay" label="Compensi maturati" value={eur(data.compensi)} hint={`${eur(data.compenso_per_intervento)} a intervento`} />
        <Stat testid="pro-cems" label="Cimiteri assegnati" value={data.cemeteries.length} />
      </div>

      <section data-testid="pro-giro">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h2 className="font-serif text-2xl text-navy">Giro del giorno</h2>
          <p className="text-sm text-muted-soft capitalize" data-testid="giro-today-label">
            {new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </p>
        </div>
        {giri.length === 0 ? (
          <EmptyState title="Nessun intervento da effettuare oggi" />
        ) : (
          <div className="space-y-5">
            {giri.map((g) => (
              <div key={g.comune} className="card-soft p-5" data-testid={`giro-comune-${g.comune}`}>
                <div className="flex items-baseline justify-between mb-3">
                  <p className="eyebrow">Giro {g.comune}</p>
                  <p className="text-xs font-bold text-[#4a7c59]">{g.rows.length} interventi</p>
                </div>
                <div className="space-y-2">
                  {g.rows.map((i, idx) => (
                    <Link key={i.id} to={`/area-pro/interventi/${i.id}`} data-testid={`giro-item-${i.id}`}
                      className="flex items-center gap-3 py-2.5 min-h-[44px] border-b border-[#f1f5f9] last:border-0">
                      <span className="w-7 h-7 rounded-full bg-[#4a7c59] text-white text-xs font-bold flex items-center justify-center shrink-0">{idx + 1}</span>
                      {(i.grave?.photo || i.grave?.admin_photo) && (
                        <img src={i.grave.admin_photo || i.grave.photo} alt="Foto luogo" className="w-10 h-10 rounded-lg object-cover shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-navy text-sm truncate">{fmtTime(i.scheduled_date)} — {i.customer_name}</p>
                        <p className="text-xs text-muted-soft truncate">{i.service} · {i._cemeteryName}{i.grave ? ` · ${i.grave.deceased_name} ${i.grave.deceased_surname}` : ""}</p>
                      </div>
                      <ProStatusBadge status={i.status} />
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        {completedToday.length > 0 && (
          <div className="card-soft p-5 mt-5" data-testid="giro-completati">
            <p className="eyebrow mb-3">Completati oggi</p>
            <div className="space-y-2">
              {completedToday.map((i) => (
                <Link key={i.id} to={`/area-pro/interventi/${i.id}`} data-testid={`giro-done-${i.id}`}
                  className="flex items-center gap-3 py-2.5 min-h-[44px] border-b border-[#f1f5f9] last:border-0">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-navy text-sm truncate">{i.customer_name} — {i.service}</p>
                    <p className="text-xs text-muted-soft truncate">{i.cemetery?.name}</p>
                  </div>
                  <ProStatusBadge status={i.status} />
                </Link>
              ))}
            </div>
          </div>
        )}
      </section>

      <section data-testid="pro-calendar-entry">
        <button onClick={() => setCalOpen(true)} data-testid="pro-calendar-entry-btn" className="card-soft p-6 w-full min-h-[56px] flex items-center justify-between gap-4 hover:shadow-md transition-shadow duration-200">
          <div className="flex items-center gap-4 text-left">
            <CalendarDays size={26} className="text-[#4a7c59] shrink-0" />
            <div>
              <p className="font-serif text-xl text-navy">Il mio calendario</p>
              <p className="text-sm text-muted-soft">{calendar.length} interventi in programma · apri la vista calendario</p>
            </div>
          </div>
          <span className="btn-secondary !py-2 text-sm shrink-0">Apri</span>
        </button>
      </section>

      <Section title="Notifiche" count={notifs.length} icon={Bell} testid="pro-notifications">
        {notifs.some((n) => !n.read) && (
          <button
            data-testid="pro-notifications-read-all"
            onClick={async () => { await api.post("/pro/notifications/read-all"); load(); }}
            className="text-xs font-semibold text-[#1e3a5f] hover:underline mb-3 min-h-[44px]"
          >
            Segna tutte come lette ({unreadCount} non lette)
          </button>
        )}
        {notifs.length === 0 ? (
          <EmptyState title="Nessuna notifica" />
        ) : (
          <div className="space-y-2">
            {notifs.map((n) => (
              <div key={n.id} data-testid={`pro-notif-${n.id}`}
                className={`card-soft p-4 text-sm ${n.read ? "text-muted-soft" : "text-navy border-l-4 border-[#4a7c59]"}`}>
                <p>{n.message || n.request_type}</p>
                <p className="text-xs text-muted-soft mt-0.5">{fmtDate(n.date)}</p>
                {n.ref_id && ["nuovo_intervento", "promemoria_intervento"].includes(n.request_type) && (
                  <Link to={`/area-pro/interventi/${n.ref_id}`} data-testid={`pro-notif-link-${n.id}`}
                    className="inline-block mt-2 text-xs font-bold text-[#1e3a5f] hover:underline uppercase tracking-wide">
                    Vedi intervento
                  </Link>
                )}
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Interventi da effettuare" count={data.upcoming.length} icon={ClipboardList} testid="pro-upcoming">
        {data.upcoming.length === 0 ? (
          <EmptyState title="Nessun intervento assegnato al momento" />
        ) : (
          <div className="space-y-3">
            {data.upcoming.map((i) => (
              <Link key={i.id} to={`/area-pro/interventi/${i.id}`} data-testid={`pro-intervention-${i.id}`}
                className="card-soft p-5 min-h-[44px] flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <p className="font-semibold text-navy">{i.service}{i.flower_name ? ` · ${i.flower_name}` : ""}</p>
                  <p className="text-sm text-muted-soft">
                    {i.cemetery?.name} · {i.grave ? `${i.grave.deceased_name} ${i.grave.deceased_surname} (campo ${i.grave.campo || "—"}, fila ${i.grave.fila || "—"}, n. ${i.grave.numero || "—"})` : ""}
                  </p>
                  <p className="text-xs text-muted-soft mt-0.5">
                    {i.scheduled_date ? `Programmato: ${fmtDate(i.scheduled_date)}` : "In attesa di programmazione"} · Cliente: {i.customer_name}
                  </p>
                </div>
                <ProStatusBadge status={i.status} />
              </Link>
            ))}
          </div>
        )}
      </Section>

      <Section title="Interventi completati" count={completed.length} icon={CheckCircle2} testid="pro-completed">
        {completed.length === 0 ? (
          <EmptyState title="Nessun intervento completato" />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {completed.map((i) => (
              <Link key={i.id} to={`/area-pro/interventi/${i.id}`} data-testid={`pro-completed-${i.id}`} className="card-soft overflow-hidden block">
                {i.final_photo && <img src={i.final_photo} alt="Foto intervento" className="w-full aspect-video object-cover" />}
                <div className="p-4">
                  <p className="text-sm font-semibold text-navy">{fmtDate(i.completed_at)} · {i.service}</p>
                  <p className="text-xs text-muted-soft">{i.cemetery?.name}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </Section>

      <Section title="Clienti assegnati" count={customers.length} icon={Users} testid="pro-customers">
        {customers.length === 0 ? (
          <EmptyState title="Nessun cliente assegnato al momento" />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {customers.map((c) => (
              <div key={c.customer_id} className="card-soft p-5" data-testid={`pro-customer-${c.customer_id}`}>
                <p className="font-semibold text-navy">{c.customer_name}</p>
                <p className="text-xs text-muted-soft mt-0.5">{c.comuni.join(", ") || "—"}</p>
                <p className="text-xs text-[#4a7c59] font-semibold mt-1">{c.interventions_count} interventi</p>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Profilo" icon={UserRound} testid="pro-profile">
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
          <div><p className="label-soft">Nome</p><p className="text-navy font-medium">{user?.name} {user?.surname}</p></div>
          <div><p className="label-soft">Email</p><p className="text-navy font-medium">{user?.email}</p></div>
          <div><p className="label-soft">Telefono</p><p className="text-navy font-medium">{user?.phone || "—"}</p></div>
          <div><p className="label-soft">Comuni in esclusiva</p><p className="text-navy font-medium">{data.comuni?.join(", ") || "—"}</p></div>
        </div>
      </Section>

      {calOpen && <ProCalendar onClose={() => { setCalOpen(false); load(); }} />}
      {todoOpen && <ProDaFare onClose={() => setTodoOpen(false)} />}
    </div>
  );
}
